import { AdditionalInterestType } from "@covertree/new-business-graphql";
import { OptionType } from "@covertree/types";
import { getLenderName, Lender } from "@covertree/features";

import { PaymentPlanKey } from "../types";

export const getMaxInterests = (
  selectedPaymentPlanKey?: PaymentPlanKey,
  isTenantFlow?: boolean
): Record<AdditionalInterestType, number> => ({
  [AdditionalInterestType.Lienholder]: isTenantFlow
    ? 0
    : selectedPaymentPlanKey === "mortgageBill"
      ? 1
      : 2,
  [AdditionalInterestType.PropertyManagerParkOwner]: 2,
  [AdditionalInterestType.HomeownersAssociation]: 1,
  [AdditionalInterestType.GovernmentMunicipality]: 2,
  [AdditionalInterestType.NotListed]: 2,
});

/**
 * Maps lender data to an additional interest object with billing information
 * @param lender - Lender data with name and state as OptionType objects
 * @param loanNumber - The loan account number
 * @returns Additional interest object with billing information
 */
export const mapLenderToBillingAdditionalInterest = (
  lender: Omit<Lender, "name" | "state"> & {
    name: Omit<OptionType, "icon"> | string;
    state: Omit<OptionType, "icon"> | string;
  },
  loanNumber: string
) => ({
  accountNumber: loanNumber,
  city: lender.city,
  name: getLenderName(
    typeof lender.name === "string" ? lender.name : lender.name.value
  ),
  state: typeof lender.state === "string" ? lender.state : lender.state.value,
  streetAddress: lender.streetAddress,
  zipCode: lender.zip,
  type: AdditionalInterestType.Lienholder,
  ...(lender.email ? { email: lender.email } : {}),
  ...(lender.unit && lender.unit.length > 0 ? { lotUnit: lender.unit } : {}),
  isBilling: true,
});

/**
 * Maps lender data to a lender details object used in the Mortgage Bill Payment payload
 * @param lender - Lender data with name and state as OptionType objects
 * @param loanNumber - The loan account number
 * @returns Lender details object with name, loan number, and address information
 */
export const mapLenderToLenderDetails = (
  lender: Omit<Lender, "name" | "state"> & {
    name: Omit<OptionType, "icon">;
    state: Omit<OptionType, "icon">;
  },
  loanNumber: string
) => ({
  lenderName: getLenderName(lender.name.value),
  loanNumber,
  ...(lender.email ? { email: lender.email } : {}),
  lenderAddress: {
    streetAddress: lender.streetAddress,
    zipCode: lender.zip,
    city: lender.city,
    state: lender.state.value,
    ...(lender.unit && lender.unit.length > 0
      ? { unitNumber: lender.unit }
      : {}),
  },
});
